import React, { useState, useEffect } from "react";
import MaterialListSelect from "./MaterialListSelect";
import PrintRequestPage from "./PrintRequestPage";
import axios from "axios";
import { useSelector } from "react-redux";
import { TbTrashX } from "react-icons/tb";
import { v4 as uuid } from "uuid";
import { toast } from "react-toastify";
import LoadingOverlay from "./LoadingOverlay";
import ConfirmDialog from "./ConfirmDialog";

const lbl =
  "block text-xs font-bold text-gray-600 uppercase tracking-wide mb-1";
const inp =
  "w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-orange-400";

function RequestMaterial() {
  const [selectedMatList, setSelectedMatList] = useState(null);
  const [selectedMatListName, setSelectedMatListName] = useState("");
  const [stockQty, setStockQty] = useState("");
  const [remark, setRemark] = useState("");
  const [transFor, setTransFor] = useState("");
  const { user } = useSelector((state) => state.auth);
  const [tempArray, setTempArray] = useState([]);
  const [currStock, setCurrStock] = useState(null);
  const [resetKey, setResetKey] = useState(0);
  const [shouldPrint, setShouldPrint] = useState(false);
  const [printGroupId, setPrintGroupId] = useState("");
  const [printItems, setPrintItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [uploadPending, setUploadPending] = useState(false);
  const [printFormNo, setPrintFormNo] = useState(null);

  useEffect(() => {
    if (shouldPrint) {
      const timer = setTimeout(() => setShouldPrint(false), 1000);
      return () => clearTimeout(timer);
    }
  }, [shouldPrint]);

  const handleMatListChange = (Arr) => {
    setSelectedMatList(Arr[0]);
    setSelectedMatListName(Arr[1]);
    setStockQty("");
    getCurrentStock(Arr[0]);
  };

  const handleStockQtyChange = (e) => {
    const v = e.target.value;
    if (v === "" || /^[0-9]+$/.test(v)) setStockQty(v);
  };

  const addObject = () => {
    if (!stockQty || !selectedMatList || !selectedMatListName) {
      toast.warning("Please fill in all required fields.");
      return;
    }
    if (Number(stockQty) > (currStock?.current_stock || 0)) {
      toast.warning("Requested qty exceeds available stock.");
      setStockQty("");
      return;
    }
    const newObj = {
      no: 0,
      product_id: selectedMatList,
      product_name: selectedMatListName,
      temp_qty: stockQty,
      stock_in: 0,
      stock_out: stockQty,
      transaction_type: "Pending",
      transaction_for: transFor,
      transaction_by: user.username,
      transaction_remark: remark,
    };
    const updated = tempArray.filter((i) => i.product_id !== selectedMatList);
    updated.push(newObj);
    setTempArray(updated.map((item, idx) => ({ ...item, no: idx + 1 })));
    setStockQty("");
  };

  const deleteObject = (no) =>
    setTempArray(tempArray.filter((i) => i.no !== no));

  const createFormHeader = async () => {
    const res = await axios.post(
      "https://eps.elkomprintama.com/post/api/store/request_header",
    );
    return res.data.form_id;
  };

  const insertStock = async (data) => {
    setIsLoading(true);
    try {
      const res = await axios.post(
        "https://eps.elkomprintama.com/post/api/store/stocks",
        { transactions: data },
      );
      if (res.status === 200) toast.success(res.data.msg);
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Insert failed.");
    } finally {
      setIsLoading(false);
    }
  };

  const getCurrentStock = async (product_id) => {
    if (!product_id) {
      setCurrStock(null);
      return;
    }
    setIsLoading(true);
    try {
      const res = await axios.get(
        `https://eps.elkomprintama.com/get/api/store/currentallstock/${product_id}`,
      );
      if (res.status === 200) setCurrStock(res.data.result);
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Failed to fetch stock.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (tempArray.length === 0) {
      toast.warning("Please add at least one item.");
      return;
    }
    setUploadPending(true);
    setShowConfirm(true);
  };

  return (
    <div className="flex flex-col" style={{ maxHeight: "calc(100vh - 80px)" }}>
      {isLoading && <LoadingOverlay message="Loading…" />}

      {/* Header */}
      <div className="w-full rounded-lg bg-orange-700 mb-4 flex-shrink-0">
        <label className="block uppercase text-white tracking-wide text-5xl text-center font-bold py-2">
          Request Material
        </label>
      </div>

      {/* Body card */}
      <form
        onSubmit={handleSubmit}
        className="flex-1 min-h-0 flex flex-col bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden"
      >
        {/* Form section — fixed at top */}
        <div className="flex-shrink-0 px-6 py-5 border-b border-gray-100 space-y-4">
          {/* Material selector + stock badge inline */}
          <div className="flex gap-3">
            <div className="flex-1 min-w-0">
              <MaterialListSelect
                key={resetKey}
                onChangeList={handleMatListChange}
                location_id={3}
              />
            </div>
            <div className="flex-shrink-0 flex flex-col items-center justify-center px-5 py-3 rounded-lg border bg-gray-50 border-gray-200 min-w-[130px]">
              <span className="text-gray-500 text-sm font-bold uppercase tracking-wide mb-1">
                Balance Stock
              </span>
              {currStock !== null ? (
                <span
                  className={`font-bold text-2xl ${currStock.current_stock === 0 ? "text-red-600" : "text-green-600"}`}
                >
                  {currStock.current_stock} {currStock.unit}
                </span>
              ) : (
                <span className="text-gray-300 text-2xl font-bold">—</span>
              )}
            </div>
          </div>

          {/* QTY / Remark / Customer row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className={lbl}>QTY</label>
              <input
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-lg font-bold focus:outline-none focus:ring-1 focus:ring-orange-400"
                type="text"
                placeholder="e.g. 10"
                value={stockQty}
                onChange={handleStockQtyChange}
              />
            </div>
            <div>
              <label className={lbl}>Catatan / Remark</label>
              <input
                className={inp}
                type="text"
                placeholder="Notes..."
                value={remark}
                onChange={(e) => setRemark(e.target.value.toUpperCase())}
              />
            </div>
            <div>
              <label className={lbl}>Customer</label>
              <input
                className={inp}
                type="text"
                placeholder="Customer name"
                value={transFor}
                onChange={(e) => setTransFor(e.target.value.toUpperCase())}
              />
            </div>
          </div>

          {/* Add button */}
          <button
            type="button"
            onClick={addObject}
            className="w-full py-2.5 rounded-xl text-sm font-bold text-white bg-orange-700 hover:bg-orange-800 transition"
          >
            Add to List
          </button>
        </div>

        {/* Table section — fixed header + scrollable body */}
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden px-6 pt-4">
          {/* Header — never scrolls */}
          <table className="w-full text-xs table-fixed flex-shrink-0">
            <colgroup>
              <col style={{ width: "44px" }} />
              <col />
              <col style={{ width: "60px" }} />
              <col style={{ width: "18%" }} />
              <col style={{ width: "16%" }} />
              <col style={{ width: "14%" }} />
              <col style={{ width: "44px" }} />
            </colgroup>
            <thead className="bg-gray-700 text-white uppercase">
              <tr>
                <th className="px-3 py-3 text-left border-r">No</th>
                <th className="px-3 py-3 text-left border-r">Product Name</th>
                <th className="px-3 py-3 text-center border-r">QTY</th>
                <th className="px-3 py-3 text-left border-r">Remark</th>
                <th className="px-3 py-3 text-left border-r">Customer</th>
                <th className="px-3 py-3 text-left border-r">Input By</th>
                <th className="px-3 py-3"></th>
              </tr>
            </thead>
          </table>
          {/* Body — scrolls independently */}
          <div className="flex-1 min-h-0 overflow-y-auto pb-4">
            <table className="w-full text-xs border-collapse table-fixed">
              <colgroup>
                <col style={{ width: "44px" }} />
                <col />
                <col style={{ width: "60px" }} />
                <col style={{ width: "18%" }} />
                <col style={{ width: "16%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "44px" }} />
              </colgroup>
              <tbody>
                {tempArray.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="py-12 text-center text-gray-400 italic text-sm"
                    >
                      No items added yet. Fill in the form above and click Add
                      to List.
                    </td>
                  </tr>
                ) : (
                  tempArray.map((data, i) => (
                    <tr
                      key={data.no}
                      className={`border-b hover:bg-orange-50 ${i % 2 === 0 ? "bg-gray-50" : "bg-white"}`}
                    >
                      <td className="px-3 py-3 border-r text-gray-500">
                        {data.no}
                      </td>
                      <td
                        className="px-3 py-3 border-r font-medium text-gray-700 truncate"
                        title={data.product_name}
                      >
                        {data.product_name}
                      </td>
                      <td className="px-3 py-3 border-r text-center font-bold text-orange-700">
                        {data.temp_qty}
                      </td>
                      <td
                        className="px-3 py-3 border-r text-gray-600 truncate"
                        title={data.transaction_remark}
                      >
                        {data.transaction_remark || "—"}
                      </td>
                      <td
                        className="px-3 py-3 border-r text-gray-600 truncate"
                        title={data.transaction_for}
                      >
                        {data.transaction_for || "—"}
                      </td>
                      <td className="px-3 py-3 border-r text-gray-500 truncate">
                        {data.transaction_by}
                      </td>
                      <td className="px-3 py-3 text-center">
                        <button
                          type="button"
                          onClick={() => deleteObject(data.no)}
                          className="text-red-400 hover:text-red-600 transition"
                        >
                          <TbTrashX size={18} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Submit footer — always pinned at bottom */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-gray-100 bg-gray-50">
          <button
            type="submit"
            disabled={tempArray.length === 0}
            className="w-full py-3 rounded-xl text-sm font-bold text-white bg-orange-700 hover:bg-orange-800 transition disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {tempArray.length > 0
              ? `Submit Request (${tempArray.length} item${tempArray.length !== 1 ? "s" : ""})`
              : "Submit Request"}
          </button>
        </div>
      </form>

      {showConfirm && uploadPending && (
        <ConfirmDialog
          message="Submit this material request?"
          onConfirm={async () => {
            setShowConfirm(false);
            try {
              const formId = await createFormHeader();
              const groupId = uuid();
              const updatedArray = tempArray.map((item) => ({
                ...item,
                transaction_group_id: groupId,
                form_id: formId,
              }));
              await insertStock(updatedArray);
              setPrintFormNo(formId);
              setPrintGroupId(groupId);
              setPrintItems(updatedArray);
              setShouldPrint(true);
              setRemark("");
              setStockQty("");
              setTempArray([]);
              setTransFor("");
              setResetKey((k) => k + 1);
            } catch (err) {
              console.error(err);
            } finally {
              setUploadPending(false);
            }
          }}
          onCancel={() => {
            setShowConfirm(false);
            setUploadPending(false);
          }}
        />
      )}

      {shouldPrint && (
        <PrintRequestPage
          formId={printFormNo}
          groupId={printGroupId}
          items={printItems}
          onDone={() => setShouldPrint(false)}
          useNow={true}
        />
      )}
    </div>
  );
}

export default RequestMaterial;
